import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject,
  ViewContainerRef,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { EventAttendee } from '@models/event-attendance/event-attendee.model';
import { Event } from '@models/events/event.model';
import { UserAvatarComponent } from '@modules/shared/components/user-avatar/user-avatar.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EventService } from '@services/events/event.service';
import { NotificationService } from '@services/shared/notification.service';
import { QRCodeModule } from 'angularx-qrcode';
import { finalize } from 'rxjs';
import { ScreenWidthService } from '@services/shared/screen-width.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GenericAction } from '@models/tickets/ticket-action.enum';
import { SwiperDirective } from '../../../../directives/swiper.directive';
import { TicketActionComponent } from '@modules/events/dialogs/get-bought-tickets/components/ticket-action/ticket-action.component';
import { SwiperOptions } from 'swiper/types';
import { RsvpDialog } from '@modules/events/dialogs/rsvp/rsvp.dialog';
import { RSVPOptionType } from '@models/event-attendance/rsvp-option-type.enum';
import { EventType } from '@models/events/event-type.enum';
import { EventStore } from '@services/stores/event.store';
import { GenericAfterAttendEventDialog } from '../generic-after-attend-event/generic-after-attend-event.dialog';
import { PremiumAfterAttendDialog } from '../premium-after-attend/premium-after-attend.dialog';

@Component({
  selector: 'app-rsvp-qr-code',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    QRCodeModule,
    UserAvatarComponent,
    SwiperDirective,
    TicketActionComponent,
  ],
  providers: [
    EventService,
    NotificationService,
    ScreenWidthService,
    EventStore,
  ],
  templateUrl: './rsvp-qr-code.dialog.html',
  styleUrl: './rsvp-qr-code.dialog.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RsvpQrCodeDialog {
  event?: Event;
  attendee?: EventAttendee;
  isDesktop: boolean = false;

  actionInProgress?: GenericAction;

  swiperActionsConfig: SwiperOptions = {
    slidesPerView: 'auto',
    speed: 2000,
    spaceBetween: 32,
    injectStyles: [':host .swiper { overflow: visible !important;}'],
  };

  constructor(
    public dialogRef: MatDialogRef<RsvpQrCodeDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private eventService: EventService,
    private notificationService: NotificationService,
    private screenWidthService: ScreenWidthService,
    protected translateService: TranslateService,
    private viewContainerRef: ViewContainerRef,
    private eventStore: EventStore,
    private dialog: MatDialog,
  ) {
    if (data) {
      this.event = data.event;
      this.attendee = data.attendee;
    }

    this.screenWidthService
      .isDesktop()
      .pipe(takeUntilDestroyed())
      .subscribe((isDesktop) => (this.isDesktop = isDesktop));
  }

  download() {
    if (
      !this.event ||
      !this.attendee?.uuid ||
      this.actionInProgress === GenericAction.DOWNLOAD
    ) {
      return;
    }

    this.actionInProgress = GenericAction.DOWNLOAD;

    const fileName = `${this.attendee.getDateDisplayFileName()}_${this.event.getSanitizedName()}_${
      this.attendee.attendeeNumber
    }.pdf`;

    this.eventService
      .downloadEventRsvpPdf(this.event.id, this.attendee.uuid, fileName)
      .pipe(finalize(() => (this.actionInProgress = undefined)))
      .subscribe({
        error: () => {
          this.notificationService.error(
            this.translateService.instant(
              'APP.EVENT_PAYMENT.COULD_NOT_DOWNLOAD',
            ),
          );
        },
      });
  }

  share() {
    if (
      !this.event ||
      !this.attendee?.uuid ||
      this.actionInProgress === GenericAction.SHARE
    ) {
      return;
    }

    this.actionInProgress = GenericAction.SHARE;

    const fileName = `${this.attendee.getDateDisplayFileName()}_${this.event.getSanitizedName()}_${
      this.attendee.attendeeNumber
    }.pdf`;
    this.eventService
      .downloadEventRsvpPdf(this.event.id, this.attendee.uuid, fileName)
      .pipe(finalize(() => (this.actionInProgress = undefined)))
      .subscribe({
        next: (blobWithFileName) => {
          const rawBlob =
            blobWithFileName instanceof Blob
              ? blobWithFileName
              : blobWithFileName.blob;

          if (!rawBlob || rawBlob.size === 0) {
            this.notificationService.error(
              'The rsvp pdf file is empty or invalid.',
            );
            return;
          }

          const file = new File([rawBlob], fileName, {
            type: 'application/pdf',
          });

          if (navigator.canShare({ files: [file] })) {
            navigator.share({
              title: 'Your Event RSVP Ticket',
              text: 'Here is your RSVP ticket for the event!',
              files: [file],
            });
          } else {
            this.notificationService.error(
              'Sharing is not supported on this browser.',
            );
          }
        },
        error: () => {
          this.notificationService.error(
            'Failed to download the RSVP pdf for sharing.',
          );
        },
      });
  }

  addToWallet() {
    if (
      !this.event ||
      !this.attendee?.uuid ||
      this.actionInProgress === GenericAction.ADD_TO_WALLET
    ) {
      return;
    }

    this.actionInProgress = GenericAction.ADD_TO_WALLET;

    const windowReference = window.open('about:blank', '_blank');

    this.eventService
      .getEventRsvpWalletPass(this.event.id, this.attendee.uuid)
      .pipe(finalize(() => (this.actionInProgress = undefined)))
      .subscribe({
        next: (passPageLink: string) => {
          if (windowReference) {
            windowReference.location.href = passPageLink;
          }
        },
        error: () => {
          if (windowReference) {
            windowReference.close();
          }
          this.notificationService.error(
            this.translateService.instant(
              'APP.EVENT_PAYMENT.COULD_NOT_DOWNLOAD',
            ),
          );
        },
      });
  }

  onRSVP(type: RSVPOptionType) {
    let notGoing = false;
    if (
      this.event?.type === EventType.SPORTPAGE ||
      this.event?.hasOneButtonSignUp()
    ) {
      if (type === RSVPOptionType.NO) {
        notGoing = true;
      }
    }

    if (this.event?.hasTwoButtonSignUp() && type === RSVPOptionType.YES) {
      notGoing = true;
    }

    const dialogRef = this.dialog.open(RsvpDialog, {
      viewContainerRef: this.viewContainerRef,
      maxWidth: '602px',
      maxHeight: '100vh',
      width: '100%',
      height: 'auto',
      data: {
        event: this.event,
        rsvpType: type,
        isAttendee: true,
        notGoing: notGoing,
        onWaitlist:
          this.event?.maxCapacityReached() && this.event.waitlistEnabled,
      },
      panelClass: ['normal-dialog', 'rsvp-dialog', 'overlay-theme'],
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res && this.event) {
        if (this.event.isPreviewEvent) {
          return;
        }

        if (res.refresh) {
          this.eventStore.refreshEvent(this.event.uri);
        }

        if (res.openEventAfterAttendOverlayDialog) {
          if (this.event.isPremium()) {
            this.dialog.open(PremiumAfterAttendDialog, {
              width: '100%',
              height: '100%',
              minHeight: '100dvh',
              maxWidth: '100%',
              panelClass: [
                'normal-dialog',
                'transparent-dialog',
                'premium-after-dialog',
              ],
              data: {
                eventAttendee: res.eventAttendee,
                event: this.event,
                rsvpType: res.rsvpType || type,
                onWaitlist:
                  this.event?.maxCapacityReached() &&
                  this.event.waitlistEnabled,
              },
              backdropClass: 'dialog-blur-backdrop-dark',
            });
          } else {
            this.dialog.open(GenericAfterAttendEventDialog, {
              width: '100%',
              height: '100%',
              maxWidth: '100%',
              panelClass: ['normal-dialog', 'transparent-dialog'],
              data: {
                eventAttendee: res.eventAttendee,
                event: this.event,
                rsvpType: res.rsvpType || type,
                onWaitlist:
                  this.event?.maxCapacityReached() &&
                  this.event.waitlistEnabled,
              },
              backdropClass: 'dialog-blur-backdrop-dark',
            });
          }
        }
      } else {
        if (this.event) {
          this.eventStore.refreshEvent(this.event.uri);
        }
      }
    });
  }

  executeAction(action: GenericAction) {
    switch (action) {
      case GenericAction.ADD_TO_WALLET:
        this.addToWallet();
        break;
      case GenericAction.EDIT_RSVP: {
        if (!this.event || !this.attendee) break;
        const eventRsvpOption = this.event.getAttendeeRSVPOptionById(
          this.attendee.id,
        );
        if (eventRsvpOption) this.onRSVP(eventRsvpOption.type);
        break;
      }
      case GenericAction.DOWNLOAD:
        this.download();
        break;
      case GenericAction.SHARE:
        this.share();
        break;
    }
  }

  shouldShowRsvpAction(action: GenericAction): boolean {
    switch (action) {
      case GenericAction.EDIT_RSVP:
        return !this.event?.hasOneButtonSignUp();
      default:
        return true;
    }
  }

  close() {
    this.dialogRef.close();
  }

  protected readonly TicketAction = GenericAction;
  protected readonly GenericAction = GenericAction;
}
